import React from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PolygonEntityType } from '../app/appContext';
import { isFourPointType } from '../utils/polygonTypes';
import { useT } from '@/contexts/I18nContext';
import { Kbd, useZoningHotkey } from './zoning/hotkeys';

interface DrawingStatusBlockProps {
  entityType: PolygonEntityType;
  pointCount: number;
  awaitingRackSelection: boolean;
  rackSelected: boolean;
  onCancel: () => void;
  onSave: () => void;
  onExit: () => void;
}

/** Page-level status block shown while drawing a new polygon — shares the
 *  toolbar slot with PolygonToolbar and EditToolbar (mutually exclusive). */
const DrawingStatusBlock: React.FC<DrawingStatusBlockProps> = ({
  entityType,
  pointCount,
  awaitingRackSelection,
  rackSelected,
  onCancel,
  onSave,
  onExit,
}) => {
  const { t } = useT();
  const label = t(`zoning.types.${entityType}`);
  const isFourPt = isFourPointType(entityType);
  const maxPoints = isFourPt ? 4 : undefined;
  const isShelf = entityType === 'shelf';
  const canSave = (maxPoints ? pointCount === maxPoints : pointCount >= 3) && (!isShelf || rackSelected);

  const getHint = (): string | null => {
    if (isShelf) {
      if (awaitingRackSelection) return t('drawing.hint.shelfRack');
      if (!rackSelected) return null;
      if (pointCount === 0) return t('drawing.hint.shelfStart');
      if (pointCount < 4) return t('drawing.hint.continueClockwise');
      return null;
    }
    if (entityType === 'rack') {
      if (pointCount === 0) return t('drawing.hint.rackStart');
      if (pointCount < 4) return t('drawing.hint.continueClockwise');
      return null;
    }
    if (entityType === 'table') {
      if (pointCount === 0) return t('drawing.hint.tableStart');
      if (pointCount < 4) return t('drawing.hint.continueClockwise');
      return null;
    }
    if (pointCount === 0) return t('drawing.hint.click');
    return null;
  };

  const hint = getHint();
  useZoningHotkey(['s', 'S', 'ы', 'Ы'], onSave, canSave);

  return (
    <div className="flex w-full flex-nowrap items-center gap-3">
      <span className="text-sm font-semibold text-primary">
        {t('drawing.adding')}: {label}
      </span>

      {isFourPt && !(isShelf && awaitingRackSelection) && (
        <span className="text-xs text-muted-foreground">
          {pointCount} {t('drawing.pointsOf')} {maxPoints} {t('drawing.points')}
        </span>
      )}

      {hint && (
        <span className="text-xs italic text-muted-foreground">{hint}</span>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={pointCount > 0 ? onCancel : onExit}
        >
          <X className="size-3.5" />
          <span>{t('drawing.cancel')}</span>
          <Kbd>Esc</Kbd>
        </Button>
        {canSave && (
          <Button type="button" size="sm" variant="default" onClick={onSave}>
            <Save className="size-3.5" />
            <span>{t('drawing.save')}</span>
            <Kbd>S</Kbd>
          </Button>
        )}
      </div>
    </div>
  );
};

export default DrawingStatusBlock;
